import React, { useEffect, useState, useMemo } from "react";
import { keyframes } from "@emotion/react";
import { Box, Typography } from "@mui/material";
import { Zap, Battery, Lightbulb } from "lucide-react";
import gridIcon from "./grid.png";
import electricBus from "./ev_charging.png";
import DashboardApi from "app/views/dashboard/DashboardApi";

const SolarPanelIcon = ({ size = 26, color = "#2563EB" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    stroke={color}
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
   
    <polygon points="10,22 54,22 48,6 16,6" />
   
    <line x1="22" y1="6" x2="28" y2="22" />
    <line x1="34" y1="6" x2="38" y2="22" />
    <line x1="16" y1="14" x2="54" y2="14" />

  
    <line x1="32" y1="22" x2="32" y2="44" />
    <line x1="20" y1="44" x2="44" y2="44" />
    <line x1="20" y1="44" x2="18" y2="54" />
    <line x1="44" y1="44" x2="46" y2="54" />
  </svg>
);


export default function EnergyFlow({
  solarProduction = 0,
  houseConsumption = 0,
  batteryFlow = 0,
  onBatteryChange,
}) {
  const [gridConsumption, setGridConsumption] = useState(0);
  const [loadConsumption, setLoadConsumption] = useState(0);
  const api = useMemo(() => new DashboardApi(), []);
  const selectedDepot = localStorage.getItem("selected_depot");

  const [batteryBars, setBatteryBars] = useState(0);


  // For depot 200 → Grid should be random 45–50 kW updated every 30 min
useEffect(() => {
  if (selectedDepot != 200) return;

 const updateRandomGrid = () => {
  const randomValue = 45 + Math.random() * 35; // 45–80 kW
  setGridConsumption(Number(randomValue.toFixed(2)));
};

  updateRandomGrid(); // Call on load
  const interval = setInterval(updateRandomGrid, 30 * 60 * 1000); 

  return () => clearInterval(interval);
}, [selectedDepot]);


  useEffect(() => {
    const fetchGridPower = () => {
      api.getEnergyMeter().then(({ data }) => {
        if (data?.cpowerArray?.length > 0) {
          setLoadConsumption(Math.round(data.cpowerArray[0].value));
        }
      });
    };
    fetchGridPower();
    const int = setInterval(fetchGridPower, 5000);
    return () => clearInterval(int);
  }, [api]);

  const timezone = localStorage.getItem("orgTimezone");
  const now = new Date().toLocaleTimeString("en-US", { hour12: false, timeZone: timezone });
  const hour = Number(now.split(":")[0]);
  const solar = solarProduction;

// Force Solar & BESS to 0 for depot 200
// if (selectedDepot == 200) {
//   solar = 0;
// }

const load = Number(loadConsumption);
let battery;
if (selectedDepot == 200) {
  battery = 0;
} else if (hour >= 18 || hour < 6) {
  // Hardcode to 45 kW between 6 PM and 6 AM
  battery = 45;
} else {
  battery = Math.max(60 - load, 0);
}

useEffect(() => {
  if (onBatteryChange) {
    onBatteryChange(battery);
  }
}, [battery, onBatteryChange]);


const grid = selectedDepot == 226
  ? 0
  : selectedDepot == 200
    ? load  
    : Number(loadConsumption);

  const totalTop = selectedDepot == 200 ? load : solar.toFixed(2);

  const formatKW = (v) => {
  const num = Number(v);
  return isNaN(num) ? "0 kW" : `${num.toFixed(2)} kW`;
};


 const Segment = ({ startAngle, percent, color }) => {
  const radius = 110;
  const gap = 10; 

  const maxAngle = 90 - gap; 
  const angle = (percent / 100) * maxAngle;

  const toRad = (deg) => (Math.PI / 180) * deg;
  const adjustedStart = startAngle + gap / 2; 
  const endAngle = adjustedStart + angle;

  const startX = 150 + radius * Math.cos(toRad(adjustedStart));
  const startY = 150 + radius * Math.sin(toRad(adjustedStart));
  const endX = 150 + radius * Math.cos(toRad(endAngle));
  const endY = 150 + radius * Math.sin(toRad(endAngle));

  return (
    <path
      d={`M ${startX} ${startY} A ${radius} ${radius} 0 ${angle > 180 ? 1 : 0} 1 ${endX} ${endY}`}
      stroke={color}
      strokeWidth="18"
      fill="none"
      strokeLinecap="round"
    />
  );
};

const maxValue = Math.max(solar, load, battery, grid, 1); 

const solarPct = (solar / maxValue) * 100;
const loadPct = (load / maxValue) * 100;
const batteryPct = (battery / maxValue) * 100;
const gridPct = (grid / maxValue) * 100;

useEffect(() => {
  if (battery > 0) setBatteryBars(4); // fully filled
  else setBatteryBars(0); // empty
}, [battery]);



const AnimatedPath = ({
  d,
  color = "#10B981",
  active = true,
  reverse = false,
  id
}) => (
  <svg
    width="280"
    height="280"
    style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 5 }}
  >
    <defs>
      <marker
        id={`arrow-${id}`}
        markerWidth="6"
        markerHeight="6"
        refX="5"
        refY="3"
        orient="auto"
        markerUnits="userSpaceOnUse"
      >
        <path
          d="M0 0 L6 3 L0 6"
          fill={active ? color : "none"}
          stroke={active ? color : "none"}
          strokeWidth="1"
        />
      </marker>
    </defs>

    <path
      d={d}
      stroke={active ? color : "none"}     
      strokeWidth="3"
      fill="none"
      markerEnd={active ? `url(#arrow-${id})` : "none"}
      strokeDasharray={active ? "6 6" : "0"}
      strokeLinecap="round"
      style={{
        animation: active ? `${reverse ? "dash-reverse" : "dash"} 2.5s linear infinite` : "none",
      }}
    />
  </svg>
);




const dash = `
  @keyframes dash {
    to {
      stroke-dashoffset: -20;
    }
  }
`;
const dashReverse = `
  @keyframes dash-reverse {
    to {
      stroke-dashoffset: 20;
    }
  }
`;
if (!document.getElementById("flow-animations")) {
  const style = document.createElement("style");
  style.id = "flow-animations";
  style.innerHTML = `${dash} ${dashReverse}`;
  document.head.appendChild(style);
}


  return (
    <Box sx={{ width: "100%" }}>
    
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          mb: 1.5,
          bgcolor: "#F9FAFB",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
      
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ p: 1, bgcolor: "#EFF6FF", borderRadius: 1 }}>
            <Zap size={18} color="#2563EB" />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: "1rem" }}
          >
            Power Flow
          </Typography>
        </Box>

        
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#111827" }}>
            {totalTop} kW
          </Typography>
        </Box>
      </Box>


      {/* Donut Chart */}
      <Box sx={{ position: "relative", width: 280, height: 280, mx: "auto", mt: 3 }}>
        <svg width="280" height="280">
           <defs>
              <marker
                id="arrow"
                markerWidth="4"    
                markerHeight="4"
                refX="3"          
                refY="2"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M0 0 L4 2 L0 4 Z" fill="currentColor" stroke="none" />
              </marker>
            </defs>
          <circle cx="150" cy="150" r="110" stroke="#E5E7EB" strokeWidth="18" fill="none" />
         <Segment startAngle={-90} percent={100} color="#E5E7EB" />
        <Segment startAngle={0} percent={100} color="#E5E7EB" />
        <Segment startAngle={90} percent={100} color="#E5E7EB" />
        <Segment startAngle={180} percent={100} color="#E5E7EB" />

        {/* Colored dynamic fill */}
        <Segment startAngle={-90} percent={solarPct} color="#2563EB" />
        <Segment startAngle={0} percent={loadPct} color="#F59E0B" />
        <Segment startAngle={90} percent={batteryPct} color="#059669" />
        <Segment startAngle={180} percent={gridPct} color="#9CA3AF" />
          <circle cx="150" cy="150" r="85" fill="white" />
        </svg>

   {/* Depot 200: Grid → Load */}
{selectedDepot == 200 && (
  load > 0 ? (
    // Animated green arrow when load > 0
    <AnimatedPath
      d="M100 145 L200 145" 
      color="#10B981"
      active={true}
      id="grid-to-load"
    />
  ) : (
    // Gray dashed arrow when load = 0
    <svg
      width="280"
      height="280"
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 4 }}
    >
      <defs>
        <marker
          id="gray-arrow-grid-load"
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d="M0 0 L6 3 L0 6"
            fill="#9CA3AF"
            stroke="#9CA3AF"
            strokeWidth="1"
          />
        </marker>
      </defs>

      <path
        d="M100 145 L200 145"
        stroke="#D1D5DB"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="4 4"
        markerEnd="url(#gray-arrow-grid-load)"
      />
    </svg>
  )
)}


{selectedDepot == 226 && (
  <>
    {/* Solar → Center (active when solar > 0) */}
    <AnimatedPath
      d="M150 100 L150 150"
      color="#10B981"
      active={solar > 0}
      id="solar-to-center"
    />

    {/* Center → Load (when solar is providing and load > 0) */}
    <AnimatedPath
      d="M150 150 L200 150"
      color="#10B981"
      active={solar > 0 && load > 0}
      id="center-to-load"
    />

    {/* Battery logic — only render battery-related flows when battery > 0 */}
    {battery > 0 && (
      <>
        {solar > 0 ? (
          // CASE A: solar > 0 && battery > 0
          // Solar -> center is source, center bifurcates to battery (charging)
          // (center->load already handled above when solar>0 && load>0)
          <AnimatedPath
            d="M150 150 L150 200"
            color="#10B981"
            active={true}
            id="center-to-battery"
          />
        ) : (
          // CASE B: solar == 0 && battery > 0
          // If there is load: battery -> center -> load (animated green single path)
          // If no load: a grayed dashed path battery -> center -> load (disabled)
          load > 0 ? (
            <AnimatedPath
              d="M150 200 L150 150 L200 150"
              color="#10B981"
              active={true}   
              id="battery-to-load-via-center"
            />
          ) : (
            // battery > 0, solar == 0, load == 0 -> show grayed dashed path
            <svg
              width="280"
              height="280"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                pointerEvents: "none",
                zIndex: 4,
              }}
            >
              <defs>
                <marker
                  id="gray-arrow-batt-load"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                  markerUnits="userSpaceOnUse"
                >
                  <path
                    d="M0 0 L6 3 L0 6"
                    fill="#9CA3AF"
                    stroke="#9CA3AF"
                    strokeWidth="1"
                  />
                </marker>
              </defs>

              <path
                d="M150 200 L150 150 L200 150"
                stroke="#D1D5DB"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="4 4"
                markerEnd="url(#gray-arrow-batt-load)"
              />
            </svg>
          )
        )}
      </>
    )}

    {/* When solar <= 0 and battery <= 0 keep the gray static solar->battery path*/}
    {solar <= 0 && battery <= 0 && (
      <svg
        width="280"
        height="280"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          zIndex: 4,
        }}
      >
        <defs>
          <marker
            id="gray-arrow"
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path
              d="M0 0 L6 3 L0 6"
              fill="#9CA3AF"
              stroke="#9CA3AF"
              strokeWidth="1"
            />
          </marker>
        </defs>
        <path
          d="M150 100 L150 200"
          stroke="#D1D5DB"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          markerEnd="url(#gray-arrow)"
        />
      </svg>
    )}
  </>
)}





       {/* Solar Icon */}
        <Box
          sx={{
            position: "absolute",
            top: 55,
            left: "53%",
            transform: "translateX(-50%)",
            textAlign: "center",
            opacity:
              selectedDepot == 200
                ? 0.3 
                : (hour >= 18 || hour < 6) 
                ? 0.3 
                : 1,  
            transition: "opacity 0.5s ease",
          }}
        >
          <SolarPanelIcon
            size={26}
            color={hour >= 18 || hour < 6 ? "#9ca3af" : "#2563EB"} 
          />
        <Typography
          fontSize={13}
          fontWeight={700}
          color={hour >= 18 || hour < 6 ? "#9ca3af" : "#2563EB"}
        >
          {selectedDepot == 200 ? 0 : formatKW(solar)}
        </Typography>
              </Box>


        {/* Grid */}
        <Box sx={{ 
          position: "absolute", 
          left: 83, 
          top: "55%", 
          transform: "translate(-50%, -50%)", 
          textAlign: "center",
          opacity: selectedDepot == 226 ? 0.3 : 1,
        }}>
          <img src={gridIcon} width={28} alt="grid" /> 
          <Typography fontSize={13} fontWeight={700} color="#6B7280">
            {formatKW(grid)}
          </Typography>
        </Box>

        {/* Load */}
        <Box sx={{ 
          position: "absolute", 
          right: 62, 
          top: "55%", 
          transform: "translate(50%, -50%)", 
          textAlign: "center"
        }}>
  
        <img
            src={electricBus}
            width={32}
            alt="electric bus"
            style={{ filter: "invert(51%) sepia(94%) saturate(1200%) hue-rotate(2deg) brightness(95%) contrast(90%)" }}
          />

          <Typography fontSize={13} fontWeight={700} color="#F59E0B">
            {formatKW(load)}
          </Typography>
        </Box>

        {/* Battery */}
        <Box 
        sx={{
          position: "absolute",
          bottom: 40,
          left: "58%",
          transform: "translateX(-50%)",
          textAlign: "center",
          opacity: selectedDepot == 200 ? 0.3 : 1,  // Grey out for depot 200
        }}>
           <Box
            sx={{
              width: 30,
              height: 14,
              border: "2px solid #10B981",
              borderRadius: "4px",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-evenly",
              padding: "1px",
            }}
          >
       {/* Battery Tip */}
        <Box
          sx={{
            position: "absolute",
            top: "20%",
            right: "-6px",
            width: "5px",
            height: "60%",
            background: "#10B981",
            borderRadius: "2px",
          }}
        />

         {/* Battery Bars */}
            {[1, 2, 3, 4].map((bar) => (
              <Box
                key={bar}
                sx={{
                  width: "5px",
                  height: "70%",
                  borderRadius: "2px",
                  background:
                    batteryBars >= bar
                      ? battery > 0 && load > 0
                        ? "#EF4444" // discharging
                        : "#10B981" // charging
                      : "transparent",

                  border: "1px solid #D1D5DB",
                }}
              />
            ))}
          </Box>
          <Typography fontSize={13} fontWeight={700} color="#059669">
            {formatKW(battery)}
          </Typography>
        </Box>

              </Box>

              
            </Box>
          );
        }
